import React, { useEffect } from 'react';

const overlay = {
  position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)',
  zIndex: 400, display: 'flex', alignItems: 'flex-end',
};
const sheet = {
  background: 'var(--color-surface)',
  borderRadius: '16px 16px 0 0',
  padding: '1.25rem 1.25rem 2.5rem',
  width: '100%',
  maxWidth: 560,
  margin: '0 auto',
  maxHeight: '90vh',
  overflowY: 'auto',
  display: 'flex',
  flexDirection: 'column',
  gap: '0.9rem',
};
const input = {
  padding: '0.55rem 0.75rem', borderRadius: 8,
  border: '1px solid var(--color-border)',
  background: 'var(--color-surface2)',
  color: 'var(--color-text)',
  fontSize: '0.92rem', width: '100%',
};
const label = { fontSize: '0.78rem', color: 'var(--color-muted)', display: 'block', marginBottom: 4 };

/**
 * Generic bottom sheet for editing / deleting an entry.
 *
 * Props:
 *   open        boolean
 *   onClose     () => void
 *   title       string
 *   fields      Array<{ key, label, type?, options?, required? }>
 *   values      object  (keyed by field.key)
 *   onChange    (key, value) => void
 *   onSave      () => Promise<void>
 *   onDelete    () => Promise<void>
 *   saving      boolean
 *   error       string
 */
export default function EntrySheet({ open, onClose, title, fields, values, onChange, onSave, onDelete, saving, error }) {
  const [confirmDelete, setConfirmDelete] = React.useState(false);

  useEffect(() => {
    if (!open) setConfirmDelete(false);
  }, [open]);

  if (!open) return null;

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave();
  };

  return (
    <div style={overlay} onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={sheet}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontWeight: 700, fontSize: '1.05rem' }}>{title}</span>
          <button
            type="button" onClick={onClose}
            style={{ background: 'none', border: 'none', color: 'var(--color-muted)', fontSize: '1.3rem', cursor: 'pointer', padding: '0.2rem 0.4rem' }}
          >✕</button>
        </div>

        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          {fields.map(f => (
            <div key={f.key}>
              <label style={label}>{f.label}</label>
              {f.options ? (
                <select
                  value={values[f.key] ?? ''}
                  onChange={e => onChange(f.key, e.target.value)}
                  style={input}
                >
                  {f.options.map(o => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              ) : (
                <input
                  type={f.type || 'text'}
                  step={f.type === 'number' ? 'any' : undefined}
                  min={f.type === 'number' ? '0' : undefined}
                  required={f.required}
                  value={values[f.key] ?? ''}
                  onChange={e => onChange(f.key, e.target.value)}
                  style={input}
                />
              )}
            </div>
          ))}

          {error && <div style={{ color: 'var(--color-danger)', fontSize: '0.85rem' }}>{error}</div>}

          <button
            type="submit" disabled={saving}
            style={{
              background: 'var(--color-primary)', color: '#fff', border: 'none',
              borderRadius: 8, padding: '0.65rem', fontWeight: 600,
              cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.7 : 1,
            }}
          >
            {saving ? 'Saving…' : 'Save Changes'}
          </button>
        </form>

        <div style={{ borderTop: '1px solid var(--color-border)', paddingTop: '0.75rem' }}>
          {confirmDelete ? (
            <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
              <span style={{ fontSize: '0.88rem', color: 'var(--color-muted)', flex: 1 }}>Delete this entry?</span>
              <button
                type="button" onClick={() => setConfirmDelete(false)}
                style={{ background: 'transparent', border: '1px solid var(--color-border)', borderRadius: 7, padding: '0.4rem 0.85rem', cursor: 'pointer', fontSize: '0.85rem' }}
              >Cancel</button>
              <button
                type="button" disabled={saving} onClick={onDelete}
                style={{ background: 'var(--color-danger)', color: '#fff', border: 'none', borderRadius: 7, padding: '0.4rem 0.85rem', fontWeight: 600, cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.7 : 1, fontSize: '0.85rem' }}
              >Yes, delete</button>
            </div>
          ) : (
            <button
              type="button" onClick={() => setConfirmDelete(true)}
              style={{ background: 'transparent', border: 'none', color: 'var(--color-danger)', cursor: 'pointer', fontSize: '0.88rem', padding: 0, fontWeight: 600 }}
            >Delete entry</button>
          )}
        </div>
      </div>
    </div>
  );
}
